import React, { useState, useEffect } from 'react';
import {
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  Box,
  Card,
  CardContent,
  Chip,
  Avatar,
  Grid,
  Paper,
  Fade,
  Zoom,
  Grow,
  CircularProgress,
  Slide,
  IconButton,
  Skeleton,
  InputAdornment,
  AvatarGroup,
  Stack,
} from '@mui/material';
import {
  Business,
  Add,
  TrendingUp,
  Star,
  BusinessCenter,
  MonetizationOn,
  CheckCircle,
  Person,
  Close,
  ArrowBack,
  ArrowForward,
  DeleteForever,
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { useThemeMode } from '../contexts/ThemeContext';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import CatLoadingSpinner from '../components/CatLoadingSpinner';
import CatEmptyState from '../components/CatEmptyState';
import { useCatAssistant } from '../contexts/CatAssistantContext';
import { Search as SearchIcon, Sort as SortIcon, HelpOutline } from '@mui/icons-material';
import LynaAvatar from '../components/LynaAvatar';
import '../cat-theme.css';

const BUSINESS_TEMPLATES: BusinessTemplate[] = [
  { id: 'hotel', name: 'Hotel', description: 'Gestión completa para hoteles y hospedajes', icon: '🏨', boards: [{ id: 1 }] },
  { id: 'salon', name: 'Salón de Belleza', description: 'Citas para servicios de estética y peluquería', icon: '💄', boards: [{ id: 1 }] },
  { id: 'gym', name: 'Gimnasio', description: 'Clases y reservas de equipos deportivos', icon: '💪', boards: [{ id: 1 }] },
  { id: 'sports', name: 'Centro Deportivo', description: 'Canchas y actividades deportivas', icon: '⚽', boards: [{ id: 1 }] },
  { id: 'camping', name: 'Camping', description: 'Reservas de parcelas y cabañas', icon: '⛺', boards: [{ id: 1 }] },
  { id: 'medical', name: 'Consultas Médicas', description: 'Turnos para consultorios y clínicas', icon: '⚕️', boards: [{ id: 1 }] },
  { id: 'lawyer', name: 'Estudio Jurídico', description: 'Citas con abogados y asesores legales', icon: '⚖️', boards: [{ id: 1 }] },
  { id: 'photography', name: 'Fotografía', description: 'Sesiones fotográficas y eventos', icon: '📸', boards: [{ id: 1 }] },
  { id: 'mechanic', name: 'Taller Mecánico', description: 'Turnos para reparación de vehículos', icon: '🔨', boards: [{ id: 1 }] },
];

interface Business {
  id: string;
  name: string;
  description: string;
}

interface BusinessTemplate {
  id: string;
  name: string;
  description: string;
  icon: string;
  boards: any[];
}

interface DashboardStats {
  totalReservations: number;
  confirmedReservations: number;
  pendingReservations: number;
  cancelledReservations: number;
  totalBoards: number;
  recentReservations: any[];
}

const Dashboard: React.FC = () => {
  const [businesses, setBusinesses] = useState<Business[]>([]);
  const [templates, setTemplates] = useState<BusinessTemplate[]>([]);
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [open, setOpen] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState<string>('');
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [currentStep, setCurrentStep] = useState<1 | 2>(1);
  const [isStatsLoading, setIsStatsLoading] = useState(true);

  const { user } = useAuth();
  const navigate = useNavigate();
  const { mode: themeMode } = useThemeMode();
  const isDark = themeMode === 'dark';
  const { say, celebrate } = useCatAssistant();

  const fetchStats = async (): Promise<DashboardStats | null> => {
    try {
      setIsStatsLoading(true);
      const response = await axios.get('/api/dashboard/stats');
      setStats(response.data);
      return response.data;
    } catch (error) {
      console.error('Error fetching dashboard stats:', error);
      return null;
    } finally {
      setIsStatsLoading(false);
    }
  };

  const fetchBusinesses = async () => {
    try {
      const response = await axios.get('/api/businesses');
      setBusinesses(response.data);
    } catch (err: any) {
      console.log('Backend not available, running in demo mode');
      setBusinesses([]);
    }
  };

  const fetchTemplates = async () => {
    try {
      const response = await axios.get('/api/businesses/templates');
      setTemplates(response.data);
    } catch (err: any) {
      console.error('Error loading templates, using fallbacks:', err);
      setTemplates(BUSINESS_TEMPLATES);
    }
  };

  const loadDashboardData = React.useCallback(async () => {
    setIsLoading(true);
    try {
      const dashboardStats = await fetchStats();
      await Promise.all([fetchBusinesses(), fetchTemplates()]);

      if (dashboardStats && user) {
        const total = dashboardStats.totalReservations || 0;
        const pending = dashboardStats.pendingReservations || 0;

        setTimeout(() => {
          if (total === 0) {
            say(`¡Bienvenido ${user.name}! 🐾 ¡Hoy es un gran día para empezar algo nuevo!`, 'greeting');
          } else if (pending > 0) {
            say(`¡Miau! Tienes ${pending} cosas pendientes. ¡Tú puedes con todo! 💪`, 'alert');
          } else {
            celebrate();
            say(`¡Todo en orden, ${user.name}! Tus negocios están ronroneando de felicidad. ✨`, 'success');
          }
        }, 800);
      }
    } catch (error) {
      console.error('Error loading dashboard data:', error);
    } finally {
      setIsLoading(false);
    }
  }, [user, say, celebrate]);

  useEffect(() => {
    loadDashboardData();
  }, [loadDashboardData]);

  const filteredBusinesses = React.useMemo(() => {
    return businesses.filter(b =>
      b.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      b.description?.toLowerCase().includes(searchQuery.toLowerCase())
    );
  }, [businesses, searchQuery]);

  const handleCloseDialog = () => {
    setOpen(false);
    setCurrentStep(1);
    setSelectedTemplate('');
    setName('');
    setDescription('');
  };

  const handleTemplateSelect = (templateId: string) => {
    setSelectedTemplate(templateId);
    setTimeout(() => setCurrentStep(2), 300);
  };

  const handleCreateBusiness = async () => {
    try {
      const response = await axios.post('/api/businesses', {
        name,
        description,
        template: selectedTemplate,
      });
      setBusinesses([...businesses, response.data]);

      // 🐱 Lyna celebrates!
      celebrate();
      say(`¡Miau! "${name}" ha sido creado con éxito. ¡A por todas! 🚀🐾`, 'celebration', 5000);

      handleCloseDialog();
    } catch (err: any) {
      console.log('Backend not available for creating businesses');
    }
  };

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
        <CatLoadingSpinner message="Los gatitos están preparando tu panel..." />
      </Box>
    );
  }

  return (
    <Box sx={{ pb: 4 }} className="cat-paw-background">
      <Box sx={{ mb: 5, mt: 2, display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 4, alignItems: 'flex-start' }}>
        <Box sx={{ flex: 1 }}>
          <Typography variant="h4" sx={{ fontWeight: 800, mb: 1, letterSpacing: '-0.5px' }}>
            ¡Miau, bienvenido de nuevo! 👋
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Aquí tienes un resumen de como están ronroneando tus negocios hoy.
          </Typography>
        </Box>
      </Box>

      {/* Analytics Section */}
      <Fade in={true} timeout={1000}>
        <Box sx={{ mb: 5 }}>
          <Grid container spacing={3}>
            {[
              {
                label: 'Reservas Totales',
                value: stats?.totalReservations || 0,
                sub: `${stats?.confirmedReservations || 0} confirmadas`,
                icon: <MonetizationOn sx={{ fontSize: 24 }} />,
                color: '#FF6B9D',
                trend: 'success.main'
              },
              {
                label: 'Agendas Activas',
                value: stats?.totalBoards || 0,
                sub: `En ${businesses.length} negocios`,
                icon: <BusinessCenter sx={{ fontSize: 24 }} />,
                color: '#3B82F6',
                trend: 'text.secondary'
              },
              {
                label: 'Pendientes',
                value: stats?.pendingReservations || 0,
                sub: `${stats?.cancelledReservations || 0} canceladas`,
                icon: <Person sx={{ fontSize: 24 }} />,
                color: '#F59E0B',
                trend: 'error.main'
              }
            ].map((stat, i) => (
              <Grid size={{ xs: 12, sm: 6, md: 4 }} key={i}>
                <Paper
                  className="cat-card"
                  sx={{
                    p: 3,
                    height: 160,
                    position: 'relative',
                    overflow: 'hidden',
                    borderRadius: '24px',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'space-between',
                    background: isDark
                      ? `linear-gradient(135deg, ${stat.color}15 0%, rgba(30,30,30,0.8) 100%)`
                      : `linear-gradient(135deg, ${stat.color}10 0%, rgba(255,255,255,0.9) 100%)`,
                    border: '1px solid',
                    borderColor: isDark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.05)',
                    backdropFilter: 'blur(10px)',
                    transition: 'transform 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                    '&:hover': { transform: 'scale(1.02) translateY(-4px)' }
                  }}
                >
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                    <Box
                      sx={{
                        p: 1.5,
                        borderRadius: '16px',
                        bgcolor: `${stat.color}20`,
                        color: stat.color,
                        display: 'flex',
                        boxShadow: `0 8px 16px ${stat.color}15`
                      }}
                    >
                      {stat.icon}
                    </Box>
                  </Box>

                  <Box>
                    <Typography variant="h3" sx={{ fontWeight: 800, fontSize: '2.2rem', mb: 0.5 }}>
                      {isStatsLoading ? <Skeleton width="40%" /> : stat.value}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600, display: 'flex', alignItems: 'center', gap: 1 }}>
                      {isStatsLoading ? <Skeleton width="60%" /> : (
                        <>
                          <span style={{ color: stat.color }}>{stat.label}</span>
                          <span style={{ opacity: 0.5 }}>•</span>
                          <span style={{ color: stat.trend as any }}>{stat.sub}</span>
                        </>
                      )}
                    </Typography>
                  </Box>
                </Paper>
              </Grid>
            ))}
          </Grid>
        </Box>
      </Fade>

      {/* Businesses Section */}
      <Fade in={true} timeout={1600}>
        <Box sx={{ mb: 4 }}>
          <Box sx={{
            display: 'flex',
            flexDirection: { xs: 'column', md: 'row' },
            justifyContent: 'space-between',
            alignItems: { xs: 'flex-start', md: 'center' },
            mb: 4,
            gap: 2
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Typography variant="h5" sx={{ fontWeight: 800, display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <BusinessCenter sx={{ color: 'primary.main' }} /> Sus Negocios
              </Typography>
              <Chip
                label={businesses.length}
                size="small"
                sx={{ bgcolor: 'primary.main', color: 'white', fontWeight: 'bold' }}
              />
            </Box>

            <Box sx={{ display: 'flex', gap: 2, width: { xs: '100%', md: 'auto' } }}>
              <TextField
                placeholder="Buscar negocio..."
                size="small"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                autoComplete="off"
                sx={{
                  flex: 1,
                  minWidth: { md: 250 },
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '12px',
                    bgcolor: isDark ? 'rgba(255,255,255,0.05)' : 'white'
                  }
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon sx={{ color: 'text.secondary', fontSize: 20 }} />
                    </InputAdornment>
                  ),
                }}
              />
              <IconButton sx={{ borderRadius: '12px', border: '1px solid', borderColor: 'divider' }}>
                <SortIcon />
              </IconButton>
              <Button
                variant="contained"
                startIcon={<Add />}
                onClick={() => setOpen(true)}
                sx={{
                  borderRadius: '12px',
                  px: 3,
                  textTransform: 'none',
                  fontWeight: 'bold',
                  boxShadow: '0 8px 16px rgba(155, 89, 182, 0.2)'
                }}
              >
                Nuevo
              </Button>
            </Box>
          </Box>

          {businesses.length === 0 ? (
            <Paper
              elevation={0}
              sx={{
                p: 8,
                borderRadius: '32px',
                bgcolor: isDark ? 'rgba(255,255,255,0.02)' : 'rgba(0,0,0,0.02)',
                border: '2px dashed',
                borderColor: isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)',
                textAlign: 'center'
              }}
            >
              <LynaAvatar size={100} />
              <Typography variant="h5" sx={{ fontWeight: 800, mb: 1 }}>¡Nada por aquí!</Typography>
              <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
                Aún no tienes ningún negocio creado. ¡Empecemos ahora!
              </Typography>
              <Button
                variant="contained"
                size="large"
                startIcon={<Add />}
                onClick={() => setOpen(true)}
                sx={{ borderRadius: '16px', px: 4, py: 1.5, fontWeight: 'bold' }}
              >
                Crear Mi Primer Negocio
              </Button>
            </Paper>
          ) : filteredBusinesses.length === 0 ? (
            <Box sx={{ textAlign: 'center', py: 8 }}>
              <Typography variant="h6" color="text.secondary">
                No encontramos ningún negocio que coincida con "{searchQuery}" 🐾
              </Typography>
              <Button sx={{ mt: 2 }} onClick={() => setSearchQuery('')}>Ver todos</Button>
            </Box>
          ) : (
            <Grid container spacing={3}>
              {filteredBusinesses.map((business, index) => (
                <Grid size={{ xs: 12, sm: 6, md: 4 }} key={business.id}>
                  <Grow in={true} timeout={100 + index * 100}>
                    <Card
                      className="cat-card"
                      onClick={() => navigate(`/business/${business.id}`)}
                      sx={{
                        height: '100%',
                        cursor: 'pointer',
                        borderRadius: '24px',
                        display: 'flex',
                        flexDirection: 'column',
                        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                        border: '1px solid',
                        borderColor: isDark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.05)',
                        overflow: 'hidden',
                        bgcolor: isDark ? 'rgba(30,30,30,0.6)' : 'white',
                        '&:hover': {
                          transform: 'translateY(-8px)',
                          boxShadow: '0 20px 40px rgba(0,0,0,0.12)',
                          borderColor: 'primary.main'
                        }
                      }}
                    >
                      <Box sx={{ p: 3, flex: 1 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                          <Avatar
                            sx={{
                              bgcolor: 'primary.main',
                              width: 52,
                              height: 52,
                              boxShadow: '0 8px 16px rgba(155, 89, 182, 0.2)'
                            }}
                          >
                            <Business />
                          </Avatar>
                          <IconButton size="small"><HelpOutline fontSize="small" /></IconButton>
                        </Box>

                        <Typography variant="h6" sx={{ fontWeight: 800, mb: 1, color: 'text.primary' }}>
                          {business.name}
                        </Typography>
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{
                            mb: 2,
                            display: '-webkit-box',
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: 'vertical',
                            overflow: 'hidden',
                            height: 40
                          }}
                        >
                          {business.description || 'Su negocio favorito espera por usted...'}
                        </Typography>

                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Chip
                            label="Ver Gestión"
                            size="small"
                            variant="outlined"
                            sx={{ borderRadius: '8px', fontWeight: 600 }}
                          />
                        </Box>
                      </Box>

                      <Box
                        sx={{
                          p: 1.5,
                          bgcolor: 'rgba(0,0,0,0.02)',
                          borderTop: '1px solid',
                          borderColor: 'divider',
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center'
                        }}
                      >
                        <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                          ID: #{String(business.id).slice(-4)}
                        </Typography>
                        <TrendingUp sx={{ fontSize: 16, color: 'success.main', opacity: 0.8 }} />
                      </Box>
                    </Card>
                  </Grow>
                </Grid>
              ))}
            </Grid>
          )}
        </Box>
      </Fade>

      {/* Create Business Dialog - Full Screen with Cinematic Transitions */}
      < Dialog
        open={open}
        onClose={handleCloseDialog}
        fullScreen
        TransitionComponent={Slide}
        /*TransitionProps={{ direction: 'up' }}*/
        PaperProps={{
          sx: {
            background: isDark ? 'linear-gradient(135deg, #1a1a2e 0%, #16213e 100%)' : 'linear-gradient(135deg, #FF6B9D 0%, #9B59B6 100%)',
            color: 'white',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            p: 4,
          },
        }}
      >
        <IconButton aria-label="close" onClick={handleCloseDialog} sx={{ position: 'absolute', right: 24, top: 24, color: 'white', bgcolor: 'rgba(255,255,255,0.1)', '&:hover': { bgcolor: 'rgba(255,255,255,0.2)' } }}>
          <Close />
        </IconButton>

        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 2, textAlign: 'center', fontSize: '2.5rem', fontWeight: 'bold', mb: 2 }}>
          Crear Nuevo Negocio <LynaAvatar size={60} />
        </DialogTitle>

        <DialogContent sx={{ maxWidth: 1000, width: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', p: 4 }}>
          {/* Step 1: Template Selection */}
          {currentStep === 1 && (
            <Fade in timeout={500}>
              <Box sx={{ width: '100%', textAlign: 'center' }}>
                <Typography variant="h5" gutterBottom sx={{ mt: 2, fontWeight: 'bold', background: isDark ? 'linear-gradient(135deg, #E0E0E0, #B0B0B0)' : 'linear-gradient(135deg, #fff, #f0f0f0)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', textShadow: isDark ? '0 2px 10px rgba(0,0,0,0.5)' : '0 2px 10px rgba(0,0,0,0.3)', mb: 2 }}>
                  Paso 1: Elige un tipo de negocio
                </Typography>
                <Typography variant="body1" sx={{ mb: 4, color: 'rgba(255,255,255,0.9)' }}>
                  Selecciona la plantilla que mejor se adapte a tu emprendimiento.
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3, justifyContent: 'center', mb: 4 }}>
                  {templates.map((template) => (
                    <Card
                      key={template.id}
                      sx={{
                        cursor: 'pointer',
                        border: selectedTemplate === template.id ? '3px solid #fff' : '2px solid rgba(255,255,255,0.3)',
                        borderRadius: 3,
                        background: selectedTemplate === template.id ? (isDark ? 'rgba(255,255,255,0.15)' : 'rgba(255,255,255,0.2)') : (isDark ? 'rgba(255,255,255,0.08)' : 'rgba(255,255,255,0.1)'),
                        backdropFilter: 'blur(10px)',
                        transition: 'all 0.4s cubic-bezier(0.4,0,0.2,1)',
                        '&:hover': {
                          transform: 'translateY(-8px) scale(1.02)',
                          boxShadow: isDark ? '0 20px 60px rgba(0,0,0,0.5)' : '0 20px 60px rgba(0,0,0,0.3)',
                          border: '2px solid rgba(255,255,255,0.6)',
                        },
                        minWidth: 280,
                        maxWidth: 280,
                      }}
                      onClick={() => handleTemplateSelect(template.id)}
                    >
                      <CardContent sx={{ textAlign: 'center', p: 3 }}>
                        <Typography variant="h2" sx={{ mb: 2, filter: isDark ? 'drop-shadow(0 4px 8px rgba(0,0,0,0.4))' : 'drop-shadow(0 4px 8px rgba(0,0,0,0.2))' }}>
                          {template.icon}
                        </Typography>
                        <Typography variant="h5" fontWeight="bold" gutterBottom sx={{ mb: 2, color: 'white' }}>
                          {template.name}
                        </Typography>
                        <Typography variant="body2" align="center" sx={{ mb: 3, minHeight: 48, color: 'rgba(255,255,255,0.8)' }}>
                          {template.description}
                        </Typography>
                        <Chip
                          label={`${template.boards.length} tablero${template.boards.length > 1 ? 'es' : ''} incluido${template.boards.length > 1 ? 's' : ''}`}
                          size="small"
                          sx={{ fontWeight: 'bold', bgcolor: isDark ? 'rgba(255,255,255,0.15)' : 'rgba(255,255,255,0.2)', color: 'white' }}
                        />
                      </CardContent>
                    </Card>
                  ))}
                </Box>
              </Box>
            </Fade>
          )}

          {/* Step 2: Business Configuration */}
          {currentStep === 2 && (
            <Fade in timeout={500}>
              <Box sx={{ width: '100%', maxWidth: 600, textAlign: 'center' }}>
                <Typography variant="h5" gutterBottom sx={{ mt: 2, fontWeight: 'bold', background: isDark ? 'linear-gradient(135deg, #E0E0E0, #B0B0B0)' : 'linear-gradient(135deg, #fff, #f0f0f0)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', textShadow: isDark ? '0 2px 10px rgba(0,0,0,0.5)' : '0 2px 10px rgba(0,0,0,0.3)', mb: 2 }}>
                  Paso 2: Configura tu negocio
                </Typography>
                <Typography variant="body1" sx={{ mb: 4, color: 'rgba(255,255,255,0.9)' }}>
                  Dale un nombre y una descripción a tu nuevo negocio.
                </Typography>
                <TextField
                  autoFocus
                  margin="dense"
                  label="Nombre del Negocio"
                  fullWidth
                  variant="outlined"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  sx={{
                    mb: 1,
                    '& .MuiOutlinedInput-root': {
                      color: 'white',
                      '& fieldset': { borderColor: 'rgba(255,255,255,0.3)' },
                      '&:hover fieldset': { borderColor: 'rgba(255,255,255,0.5)' },
                      '&.Mui-focused fieldset': { borderColor: 'white' },
                    },
                    '& .MuiInputLabel-root': { color: 'rgba(255,255,255,0.7)' },
                    '& .MuiInputLabel-root.Mui-focused': { color: 'white' },
                  }}
                />
                {name && (
                  <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.7)', display: 'block', mb: 2, textAlign: 'left', pl: 1 }}>
                    Su enlace principal será: <strong style={{ color: '#fff' }}>/{name.toString().toLowerCase().trim().replace(/\s+/g, '-').replace(/[^\w\-]+/g, '').replace(/\-\-+/g, '-').replace(/^-+/, '').replace(/-+$/, '') || 'negocio'}</strong> 
                  </Typography>
                )}
                <TextField
                  margin="dense"
                  label="Descripción (opcional)"
                  fullWidth
                  variant="outlined"
                  multiline
                  rows={3}
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  sx={{
                    mb: 3,
                    '& .MuiOutlinedInput-root': {
                      color: 'white',
                      '& fieldset': { borderColor: 'rgba(255,255,255,0.3)' },
                      '&:hover fieldset': { borderColor: 'rgba(255,255,255,0.5)' },
                      '&.Mui-focused fieldset': { borderColor: 'white' },
                    },
                    '& .MuiInputLabel-root': { color: 'rgba(255,255,255,0.7)' },
                    '& .MuiInputLabel-root.Mui-focused': { color: 'white' },
                  }}
                />
                {selectedTemplate && (
                  <Alert severity="info" sx={{ bgcolor: 'rgba(255,255,255,0.15)', color: 'white', '& .MuiAlert-icon': { color: 'white' } }}>
                    <Typography variant="body2">
                      <strong>Plantilla seleccionada:</strong> {templates.find((t) => t.id === selectedTemplate)?.name}
                    </Typography>
                    <Typography variant="body2">
                      Se crearán automáticamente {templates.find((t) => t.id === selectedTemplate)?.boards.length} tableros de reservas preconfigurados.
                    </Typography>
                  </Alert>
                )}
              </Box>
            </Fade>
          )}
        </DialogContent>

        <DialogActions sx={{ mt: 4, justifyContent: 'center', gap: 2, pb: 4 }}>
          {currentStep === 2 && (
            <Button onClick={() => setCurrentStep(1)} startIcon={<ArrowBack />} variant="outlined" sx={{ borderRadius: 2, px: 3, py: 1, color: 'white', borderColor: 'rgba(255,255,255,0.5)', '&:hover': { borderColor: 'white', bgcolor: 'rgba(255,255,255,0.1)' }, textTransform: 'none' }}>
              Anterior
            </Button>
          )}
          {currentStep === 1 && (
            <Button onClick={() => setCurrentStep(2)} disabled={!selectedTemplate} endIcon={<ArrowForward />} variant="contained" sx={{ borderRadius: 3, px: 5, py: 1.5, background: 'rgba(255,255,255,0.2)', color: 'white', boxShadow: isDark ? '0 4px 15px rgba(0,0,0,0.4)' : '0 4px 15px rgba(0,0,0,0.2)', '&:hover': { background: 'rgba(255,255,255,0.3)', boxShadow: isDark ? '0 6px 20px rgba(0,0,0,0.5)' : '0 6px 20px rgba(0,0,0,0.3)', transform: 'translateY(-2px)' }, '&:disabled': { background: 'rgba(255,255,255,0.1)', color: 'rgba(255,255,255,0.4)' }, textTransform: 'none', fontWeight: 'bold', fontSize: '1.1rem' }}>
              Siguiente
            </Button>
          )}
          {currentStep === 2 && (
            <Button onClick={handleCreateBusiness} disabled={!name.trim() || !selectedTemplate} variant="contained" sx={{ borderRadius: 3, px: 5, py: 1.5, background: 'rgba(255,255,255,0.9)', color: '#9B59B6', boxShadow: isDark ? '0 4px 15px rgba(0,0,0,0.4)' : '0 4px 15px rgba(0,0,0,0.2)', '&:hover': { background: 'white', boxShadow: isDark ? '0 6px 20px rgba(0,0,0,0.5)' : '0 6px 20px rgba(0,0,0,0.3)', transform: 'translateY(-2px)' }, '&:disabled': { background: 'rgba(255,255,255,0.3)', color: 'rgba(155, 89, 182, 0.4)' }, textTransform: 'none', fontWeight: 'bold', fontSize: '1.1rem' }}>
              Crear Negocio
            </Button>
          )}
        </DialogActions>
      </Dialog >
    </Box >
  );
};

export default Dashboard;